import { useState } from 'react';
import { Box, Alert, useTheme, Slide, Paper } from '@mui/material';
import { Modal } from '@components/Modal';
import { DndContext } from '@dnd-kit/core';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { AdminOfflineGuard } from '@components/guards/AdminOfflineGuard';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useChecklistData } from './hooks';
import { useChecklistDnd } from './hooks/useChecklistDnd';
import {
  SideEditPanel,
  ChecklistHeader,
  ChecklistErrorState,
  ChecklistDragOverlay,
  ChecklistContent
} from './components';
import type {
  ChecklistItem as ChecklistItemType,
  ChecklistFormData
} from './types';

export const PreCalChecklist = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();

  const [sideFormData, setSideFormData] = useState<{
    mode: 'add' | 'edit';
    item?: ChecklistItemType;
    position?: number;
  } | null>(null);

  const {
    items,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving,
    saveError,
    actions
  } = useChecklistData();

  const {
    sensors,
    activeItem,
    collisionDetection,
    handleDragStart,
    handleDragEnd
  } = useChecklistDnd({
    items,
    onReorderItems: actions.reorderItems
  });

  const handleSave = async () => {
    await actions.saveChanges();
  };

  const handleEdit = (item: ChecklistItemType) => {
    setSideFormData({
      mode: 'edit',
      item
    });
  };

  const handleAddAtPosition = (position: number) => {
    setSideFormData({
      mode: 'add',
      position
    });
  };

  const handleCloseSideForm = () => {
    setSideFormData(null);
  };

  const handleSideFormSubmit = async (formData: ChecklistFormData) => {
    if (sideFormData?.mode === 'edit' && sideFormData?.item) {
      await actions.updateItem(sideFormData.item.id, formData);
    } else if (sideFormData?.position !== undefined) {
      await actions.addItemAtPosition(formData, sideFormData.position);
    }
    handleCloseSideForm();
  };

  const handleDeleteItem = async (itemId: string) => {
    await actions.deleteItem(itemId);
  };

  const handleAddNew = () => {
    setSideFormData({
      mode: 'add',
      position: items.length
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isOnline) {
    return <AdminOfflineGuard children={null} />;
  }

  if (error) {
    return (
      <ChecklistErrorState
        error={error}
        itemCount={items.length}
        onRefresh={actions.refresh}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.palette.background.default
      }}
    >
      <ChecklistHeader
        itemCount={items.length}
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onAddItem={handleAddNew}
        onSave={handleSave}
        onDiscard={actions.discardChanges}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: { xs: 1, sm: 3, md: 4 },
            paddingBottom: { xs: 10, sm: 4 }, // Extra padding at the bottom for mobile
            minHeight: 0
          }}
        >
          <Box
            sx={{
              maxWidth: 800,
              margin: '0 auto',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            {saveError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {t('checklist:error.saveFailed')}: {saveError.message}
              </Alert>
            )}

            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <ChecklistContent
                  items={items}
                  isSaving={isSaving}
                  sideFormData={sideFormData}
                  onEdit={handleEdit}
                  onDelete={handleDeleteItem}
                  onAddAtPosition={handleAddAtPosition}
                  onAddNew={handleAddNew}
                />
                <ChecklistDragOverlay activeItem={activeItem ?? null} />
              </DndContext>
            </Box>
          </Box>
        </Box>

        {/* Mobile Dialog */}
        <Modal
          open={!!sideFormData}
          onClose={handleCloseSideForm}
          fullScreen
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDialog-paper': {
              margin: 0,
              maxHeight: '100%',
              width: '100%'
            }
          }}
        >
          <SideEditPanel
            mode={sideFormData?.mode || 'add'}
            item={sideFormData?.item}
            position={sideFormData?.position}
            onSubmit={handleSideFormSubmit}
            onCancel={handleCloseSideForm}
          />
        </Modal>

        {/* Desktop Slide Panel */}
        <Slide direction="left" in={!!sideFormData} mountOnEnter unmountOnExit>
          <Paper
            elevation={3}
            sx={{
              width: 400,
              height: '100%',
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              borderLeft: '1px solid',
              borderColor: 'divider',
              zIndex: 1200
            }}
          >
            <SideEditPanel
              mode={sideFormData?.mode || 'add'}
              item={sideFormData?.item}
              position={sideFormData?.position}
              onSubmit={handleSideFormSubmit}
              onCancel={handleCloseSideForm}
            />
          </Paper>
        </Slide>
      </Box>
    </Box>
  );
};
